import { REVIEW_DELETE_DIALOG_CLASS } from "./ScrollableReviewDialog";
import { useEffect, useMemo, useRef, useState } from "react";
import { normalizeAnnualReviewOutcome } from "../../helpers/annualClientActivityReview";
import { confirmAnnualClientActivity } from "../../services/client/confirmAnnualClientActivity";
import { finalizeAnnualClientActivityReview } from "../../services/client/finalizeAnnualClientActivityReview";
import { getAnnualClientActivityReview } from "../../services/client/getAnnualClientActivityReview";
import DeleteModal from "../../utils/DeleteModal";
import LoadingOverlay from "../LoadingOverlay";
import { useCelebration } from "../../context/CelebrationContext";
import { shouldCelebrateServiceCompletion } from "../../helpers/completionCelebration";
import {
    EFILE_STATUS_OPTIONS,
    getClientServiceEfileStatus,
    isEfileStatusApplicable,
    normalizeEfileStatus,
    serializeEfileStatus,
} from "../../helpers/eFileStatus";
import EfileStatusListBox from "../../pages/ManageClients/EfileStatusListBox";

const STATUS = { TO_DO: "TO_DO", IN_PROGRESS: "IN_PROGRESS" };

export const normalizeAnnualReviewStatus = (status) => {
    if (status === 0 || status === "0") return STATUS.TO_DO;
    if (status === 1 || status === "1") return STATUS.IN_PROGRESS;
    const value = String(status ?? "").trim().replace(/[\s-]+/g, "_").toUpperCase();
    if (value === "TODO" || value === "TO_DO") return STATUS.TO_DO;
    if (value === "INPROGRESS" || value === "IN_PROGRESS") return STATUS.IN_PROGRESS;
    return null;
};

const getServiceId = (service) =>
    service?.clientServiceSubscriptionId ?? service?.ClientServiceSubscriptionId ??
    service?.subscriptionId ?? service?.id ?? service?.Id;
const getServiceName = (service) =>
    service?.serviceName ?? service?.ServiceName ?? service?.name ?? "Service";
const getServiceStatus = (service) => service?.status ?? service?.Status;

const getReviewServices = (response) => {
    const services = response?.data?.review?.applicableServices ??
        response?.review?.applicableServices ?? [];
    const seen = new Set();
    return (Array.isArray(services) ? services : []).filter((service) => {
        const id = getServiceId(service);
        const status = normalizeAnnualReviewStatus(getServiceStatus(service));
        if (!id || !status || seen.has(String(id))) return false;
        seen.add(String(id));
        return true;
    });
};

export const buildAnnualReviewPayload = (clientId, decisions) => ({
    clientId,
    decisions: decisions.map((decision) => {
        const payloadDecision = {
            subscriptionId: decision.subscriptionId,
            expectedStatus: decision.expectedStatus,
            action: decision.action,
        };
        if (
            decision.action !== "DELETE" &&
            Object.prototype.hasOwnProperty.call(decision, "eFileStatus") &&
            Object.prototype.hasOwnProperty.call(decision, "expectedEFileStatus")
        ) {
            payloadDecision.eFileStatus = decision.eFileStatus;
            payloadDecision.expectedEFileStatus = decision.expectedEFileStatus;
        }
        return payloadDecision;
    }),
});

const AnnualClientActivityReviewContent = ({
    clientId,
    clientName,
    canWriteClientServices,
    canDeleteClientServices,
    onCompleted,
    onMutationStateChange,
    onRequestClose,
    celebrateServiceCompletions = true,
}) => {
    const { celebrate, showCompletionMessage } = useCelebration();
    const [phase, setPhase] = useState("question");
    const [services, setServices] = useState([]);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [decisions, setDecisions] = useState([]);
    const [efileDrafts, setEfileDrafts] = useState({});
    const [isPending, setIsPending] = useState(false);
    const [pendingAction, setPendingAction] = useState(null);
    const [isDeleteOpen, setIsDeleteOpen] = useState(false);
    const [error, setError] = useState("");
    const stagedIds = useRef(new Set());
    const finalizing = useRef(false);

    const currentService = services[currentIndex];
    const currentStatus = normalizeAnnualReviewStatus(getServiceStatus(currentService));
    const currentServiceId = getServiceId(currentService);
    const allReviewed = services.length === decisions.length;
    const mutationPending = isPending && ["confirm", "finalize"].includes(pendingAction);

    useEffect(() => {
        setPhase("question");
        setServices([]);
        setCurrentIndex(0);
        setDecisions([]);
        setEfileDrafts({});
        setIsPending(false);
        setPendingAction(null);
        setIsDeleteOpen(false);
        setError("");
        stagedIds.current = new Set();
        finalizing.current = false;
    }, [clientId]);

    useEffect(() => {
        onMutationStateChange?.(mutationPending);
        return () => onMutationStateChange?.(false);
    }, [mutationPending, onMutationStateChange]);

    const stageDecision = (action) => {
        const id = String(currentServiceId ?? "");
        if (!canWriteClientServices || (action === "DELETE" && !canDeleteClientServices) ||
            !currentServiceId || isPending || stagedIds.current.has(id)) return;
        stagedIds.current.add(id);
        const decision = {
            subscriptionId: currentServiceId,
            expectedStatus: currentStatus,
            action,
        };
        const rawEfileStatus = getClientServiceEfileStatus(currentService);
        const expectedEfileStatus = normalizeEfileStatus(rawEfileStatus);
        const selectedEfileStatus = Object.prototype.hasOwnProperty.call(efileDrafts, id)
            ? efileDrafts[id]
            : expectedEfileStatus;
        if (
            action !== "DELETE" &&
            isEfileStatusApplicable(currentService) &&
            selectedEfileStatus !== expectedEfileStatus &&
            normalizeEfileStatus(selectedEfileStatus) !== null
        ) {
            decision.eFileStatus = serializeEfileStatus(
                selectedEfileStatus,
                rawEfileStatus,
            );
            decision.expectedEFileStatus = rawEfileStatus;
        }
        setDecisions((items) => [...items, decision]);
        setCurrentIndex((index) => index + 1);
        setError("");
    };

    const loadReview = async () => {
        if (isPending) return;
        setIsPending(true);
        setPendingAction("load");
        setError("");
        const response = await getAnnualClientActivityReview(clientId);
        setIsPending(false);
        setPendingAction(null);
        if (!response?.success) {
            setError(response?.message || "The Service review could not be loaded.");
            return;
        }
        setServices(getReviewServices(response));
        setCurrentIndex(0);
        setDecisions([]);
        setEfileDrafts({});
        stagedIds.current = new Set();
        setPhase("services");
    };

    const confirmActive = async () => {
        if (isPending) return;
        setIsPending(true);
        setPendingAction("confirm");
        setError("");
        const response = await confirmAnnualClientActivity(clientId, true);
        if (!response?.success) {
            setError(response?.message || "The client could not be confirmed active.");
            setIsPending(false);
            setPendingAction(null);
            return;
        }
        try {
            await onCompleted?.({
                ...normalizeAnnualReviewOutcome(response),
                completionType: "confirm",
            });
        } catch (completionError) {
            setError(completionError?.message || "The review was saved, but the client could not be refreshed.");
        } finally {
            setIsPending(false);
            setPendingAction(null);
        }
    };

    const reloadAfterConflict = async (message) => {
        setError(message || "Service data changed while you were reviewing it. Please review the updated Services.");
        const response = await getAnnualClientActivityReview(clientId);
        if (response?.success) {
            setServices(getReviewServices(response));
            setCurrentIndex(0);
            setDecisions([]);
            setEfileDrafts({});
            stagedIds.current = new Set();
        } else {
            setError(response?.message || "Service data changed, but the updated review could not be loaded.");
        }
    };

    const finalize = async () => {
        if (!canWriteClientServices || isPending || !allReviewed || finalizing.current) return;
        finalizing.current = true;
        setIsPending(true);
        setPendingAction("finalize");
        setError("");
        const response = await finalizeAnnualClientActivityReview(
            buildAnnualReviewPayload(clientId, decisions),
        );
        if (!response?.success) {
            const code = String(response?.data?.code ?? "").toUpperCase();
            if (response?.status === 409 || response?.status === 412 ||
                code.includes("STALE") || code.includes("CONFLICT")) {
                await reloadAfterConflict(response?.message);
            } else {
                setError(response?.message || "The review could not be finalized.");
            }
            setIsPending(false);
            setPendingAction(null);
            finalizing.current = false;
            return;
        }
        try {
            if (
                celebrateServiceCompletions &&
                shouldCelebrateServiceCompletion(response)
            ) {
                celebrate();
                showCompletionMessage(
                    "Congratulations on completing the service",
                );
            }
            await onCompleted?.({
                ...normalizeAnnualReviewOutcome(response),
                completionType: "finalize",
            });
        } catch (completionError) {
            setError(completionError?.message || "The review was saved, but the client could not be refreshed.");
        } finally {
            setIsPending(false);
            setPendingAction(null);
            finalizing.current = false;
        }
    };

    const title = useMemo(() => {
        if (phase === "question") return "Is this client still active?";
        if (allReviewed) return "Service review complete";
        return "Review client Services";
    }, [allReviewed, phase]);

    const currentEfileStatus = getClientServiceEfileStatus(currentService);
    const normalizedCurrentEfileStatus = normalizeEfileStatus(currentEfileStatus);
    const selectedEfileStatus = currentServiceId &&
        Object.prototype.hasOwnProperty.call(efileDrafts, String(currentServiceId))
        ? efileDrafts[String(currentServiceId)]
        : normalizedCurrentEfileStatus;
    const efilePlaceholder = currentEfileStatus === null || currentEfileStatus === undefined ||
        String(currentEfileStatus).trim() === ""
        ? "Not Assigned"
        : "Unknown status";

    return <>
        <LoadingOverlay isLoading={isPending && pendingAction === "finalize"} message="Applying review changes..." />
        <h3 className="text-center text-lg font-medium text-gray-900">{title}</h3>
        {phase === "question" ? (
            <p className="mt-2 text-center text-sm text-gray-600">
                Confirm whether {clientName || "this client"} is still active before continuing.
            </p>
        ) : allReviewed ? (
            <p className="mt-2 text-center text-sm text-gray-600">
                All applicable Services have been reviewed. Submit your decisions to finish.
            </p>
        ) : (
            <div className="mt-4">
                <p className="text-center text-sm font-semibold text-teal-700">
                    Service {currentIndex + 1} of {services.length}
                    <span className="sr-only">
                        {currentIndex + 1} out of {services.length}
                    </span>
                </p>
                <div className="mt-4 rounded-md border border-slate-200 bg-slate-50 p-4">
                    <p className="font-medium text-slate-900">{getServiceName(currentService)}</p>
                    <p className="mt-1 text-sm text-slate-600">
                        Status: {currentStatus === STATUS.TO_DO ? "To Do" : "In Progress"}
                    </p>
                    {isEfileStatusApplicable(currentService) ? (
                        <EfileStatusListBox
                            efileStatuses={EFILE_STATUS_OPTIONS}
                            selectedEfileStatus={selectedEfileStatus}
                            placeholder={efilePlaceholder}
                            className="mt-3"
                            onChange={(value) => setEfileDrafts((drafts) => ({
                                ...drafts,
                                [String(currentServiceId)]: value,
                            }))}
                        />
                    ) : null}
                </div>
            </div>
        )}
        {error ? <div role="alert" className="mt-4 rounded-md bg-red-50 p-3 text-sm text-red-700">{error}</div> : null}
        {phase === "services" && !canWriteClientServices ? (
            <div role="alert" className="mt-4 rounded-md bg-amber-50 p-3 text-sm text-amber-900">
                <p>You don't have permission to review client Services.</p>
                <button type="button" onClick={onRequestClose}
                    className="mt-3 inline-flex justify-center rounded-md border border-amber-300 bg-white px-4 py-2 font-medium text-amber-900 hover:bg-amber-100">Close</button>
            </div>
        ) : null}
        {phase === "question" ? (
            <div className="mt-6 grid grid-cols-2 gap-3">
                <button type="button" onClick={loadReview} disabled={isPending}
                    className="inline-flex justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50">
                    {pendingAction === "load" ? "Loading..." : "No"}
                </button>
                <button type="button" onClick={confirmActive} disabled={isPending}
                    className="inline-flex justify-center rounded-md bg-teal-500 px-4 py-2 text-sm font-medium text-white hover:bg-teal-600 disabled:cursor-not-allowed disabled:opacity-50">
                    {pendingAction === "confirm" ? "Saving..." : "Yes"}
                </button>
            </div>
        ) : allReviewed ? canWriteClientServices ? (
            <button type="button" onClick={finalize} disabled={isPending}
                className="mt-6 inline-flex w-full justify-center rounded-md bg-teal-500 px-4 py-2 text-sm font-medium text-white hover:bg-teal-600 disabled:cursor-not-allowed disabled:opacity-50">
                {pendingAction === "finalize" ? "Updating client review..." : "Submit review"}
            </button>
        ) : null : canWriteClientServices ? (
            <div className="mt-6 grid grid-cols-1 gap-3 sm:grid-cols-3">
                {canDeleteClientServices ? <button type="button" onClick={() => setIsDeleteOpen(true)} disabled={isPending}
                    className="inline-flex justify-center rounded-md bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50">Delete</button> : null}
                <button type="button" onClick={() => stageDecision("KEEP_SAME")} disabled={isPending}
                    className="inline-flex justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50">Keep the same</button>
                <button type="button" onClick={() => stageDecision("COMPLETE")} disabled={isPending}
                    className="inline-flex justify-center rounded-md bg-teal-500 px-4 py-2 text-sm font-medium text-white hover:bg-teal-600 disabled:opacity-50">Complete</button>
            </div>
        ) : null}
        <DeleteModal isModalOpen={isDeleteOpen} setIsModalOpen={setIsDeleteOpen}
            onRemove={() => { setIsDeleteOpen(false); stageDecision("DELETE"); }}
            subscriptionIdToDelete={currentServiceId}
            title={`Are you sure you want to delete ${getServiceName(currentService)}?`}
            buttonText="Delete" destructive={true} dialogClassName={`z-[230] ${REVIEW_DELETE_DIALOG_CLASS}`} />
    </>;
};

export default AnnualClientActivityReviewContent;
